import { useAppSelector } from '../app/state';
import { callAction } from '../app/actions';

/**
 * THE ONE LANGUAGE PICKER (V0.53.5).
 *
 * Seven surfaces carry a language `<select>` — the top bar, the doc bar, the dashboard head,
 * the AI configuration and targets screens, the help and privacy pages — and until V0.53.5
 * they were driven two ways at once: four rendered their own controlled select with an
 * onChange, two were uncontrolled and relied on a delegated `change` listener in `chrome.ts`
 * that fired for every `.lang-picker` but the top bar's. Three of the controlled ones ALSO
 * carried the class, so a change there called `setLocale` twice, and only its early return on
 * an unchanged code made the second call harmless. This component is the one implementation:
 * controlled from the snapshot, one onChange, no delegated listener anywhere.
 *
 * The markup is byte for byte what each surface rendered — `<select class="btn lang-picker">`
 * with the caller's id, title and aria-label — because `.perm-head select.lang-picker` is
 * what the stylesheet lays the headers out by, and the specs drive every picker by id.
 * Hidden when there is one language: one language is not a choice.
 *
 * `onPicked` is for a surface that reacts to the change itself (the dashboard's "I moved"
 * ring); the locale change is never the caller's to make.
 */
export function LanguagePicker({
  id,
  className,
  title = 'Language',
  ariaLabel,
  onPicked,
}: {
  id?: string;
  /** Extra classes beside `btn lang-picker`. */
  className?: string;
  title?: string;
  ariaLabel?: string;
  onPicked?: (code: string) => void;
}) {
  const languages = useAppSelector((s) => s.languages);
  const locale = useAppSelector((s) => s.currentLocale);
  if (languages.length < 2) return null;
  return (
    <select
      className={className ? `btn lang-picker ${className}` : 'btn lang-picker'}
      id={id}
      title={title}
      aria-label={ariaLabel}
      value={locale}
      onChange={(e) => {
        const code = e.target.value;
        void callAction('setLocale', code);
        onPicked?.(code);
      }}
    >
      {languages.map((l) => (
        <option key={l.code} value={l.code}>
          {l.name}
        </option>
      ))}
    </select>
  );
}
